import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./style.css";
import { Download, DownloadIcon, Search } from "../../constants";
import { calculateDaysDifference, downloadYearWiseReport } from "../../helper-functions";
import { EmptyList } from "../empty";
import { useSelector } from "react-redux";
import { BUFFER_DAYS } from "../../constants/textconstants";
import { API_ENDPOINTS } from "../../constants/apiEndpoints";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";
import { get } from "../../config/api";
import { TESTING_DATA } from "../../constants/isTesting";

interface Props {
  onYearChange?: (year: string) => void;
  showHeaderActions?: boolean; // controls search and year selector
}

const PendingRequests: React.FC<Props> = ({
  onYearChange,
  showHeaderActions = true,
}) => {
  const navigate = useNavigate();

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear.toString());
  const years = Array.from({ length: currentYear - 2025 + 1 }, (_, i) => 2025 + i);
  const [isDownloading, setIsDownloading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [certificates, setCertificates] = useState<any[]>(data || []);
  const [isLoading, setIsLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, size: 10, total: 0 });

  const userData = useSelector((state: any) => state.formConfig.user);

  const isPendingOrderAvailable = certificates;

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        countryCode: "IN",
        status: "PENDING_APPROVAL",
        page: pagination.page.toString(),
        size: pagination.size.toString(),
      });
      if (debouncedSearchQuery) {
        params.append("searchBy", debouncedSearchQuery);
      }
       if (userData?.role) {
        params.append("userRole", userData.role);
      }
      let response:any = {};
      // console.log(userData.is_viewed_guided_tour, 'ghjkjhgfghj') // Removed console.log
      if (userData.is_viewed_guided_tour) {
        response = await get(
        `${API_ENDPOINTS.CERTIFICATE_REQUESTS_BY_USER(
          userData.userId
        )}?${params.toString()}`
      );
      } else {
        response = TESTING_DATA.PENDING_REQUESTS
      }
      
      
      
      if (response && response.items) {
        setCertificates(response.items);
        setPagination({
          page: response.page,
          size: response.size,
          total: response.total,
        });
      } else {
        setCertificates([]);
        setPagination({ page: 1, size: 10, total: 0 });
      }
    } catch (error) {
      console.error("Failed to fetch issued certificates:", error);
      setCertificates([]);
      setPagination({ page: 1, size: 10, total: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPagination((p) => ({ ...p, page: 1 })); // Reset to first page on new search
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    if(showHeaderActions==false){
      fetchData();
    }
  }, [debouncedSearchQuery, pagination.page]);



  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setYear(e.target.value);
    if (onYearChange) {
      onYearChange(e.target.value);
    }
  };

  const handleDownloadReport = async () => {
    setIsDownloading(true);
    try {
      await downloadYearWiseReport(year);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.size);

  return (
    <div className="issued-certificates" style={{ marginBottom: 20 }}>
      <div className="issued-certificates-header">
        <h3>Pending Requests</h3>
        {!showHeaderActions && isPendingOrderAvailable.length > 0 && (
          <button
            onClick={() => navigate("/all-requests/0")}
            className="viewAllButton"
          >
            View All
          </button>
        )}
        {showHeaderActions && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {isDownloading ? (
              <div className="download-spinner">Downloading...</div>
            ) : (
              <div
                onClick={handleDownloadReport}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <img src={DownloadIcon} style={{ height: 20 }} />
                <span className="downloadfull">Download Full Report</span>
              </div>
            )}

            <div className="year-selector">
              <select value={year} onChange={handleYearChange}>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {showHeaderActions && (
        <div className="issued-certificates-actions">
          <input
            type="text"
            placeholder="Search by retailer name or BRP ID"
            style={{
              backgroundImage: `url(${Search})`,
              backgroundSize: 20,
              backgroundPosition: "9px 11px",
              backgroundRepeat: "no-repeat",
              paddingLeft: 40,
            }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      )}
      {isPendingOrderAvailable.length == 0 ? (
        <EmptyList from="pending" />
      ) : (
        <table>
          <thead>
            <tr>
              <th>Firm Name</th>
              <th>BRP ID</th>
              <th>Date submitted</th>
              <th>Deadline</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {certificates
              .slice(0, showHeaderActions ? certificates.length : 3)
              .map((cert: any, index) => {
                const days =
                  calculateDaysDifference(cert.created_at) + BUFFER_DAYS;
                let dueMessage = cert.due_date_message;
                let dueClass = "";

                if (days < 0) {
                  // dueMessage = `Overdue by ${Math.abs(days)} days`;
                  dueClass = "text-red";
                } else if (days === 0) {
                  // This will now trigger when the original due date is 7 days away
                  // dueMessage = "Due today";
                  dueClass = "text-yellow";
                } else {
                  // dueMessage = `Due in ${days} days`;
                  dueClass = "text-black";
                }
                return (
                  <tr key={index}>
                    <td>{cert.shop_name ? cert.shop_name : cert.firm_name}</td>
                    <td>{cert.partner_id}</td>
                    <td>{formatDateToDDMMYYYY(cert.created_at)}</td>

                    <td className={dueClass}>{dueMessage}</td>

                    <td>
                      <span
                        className={`status-${cert.status
                          .replace(/ /g, "-")
                          .toLowerCase()}`}
                      >
                        {cert.status.toLowerCase() === "pending"
                          ? "Pending Approval"
                          : cert.status.toLowerCase() === "approved"
                          ? "Pending Delivery"
                          : cert.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="view-details-button"
                        onClick={() =>
                          navigate(`/review-request/${cert.request_id}`)
                        }
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      )}
      {showHeaderActions && totalPages > 1 && (
        <div className="pagination-controls">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default PendingRequests;
